/**
 * Every word the visitor reads.
 *
 * Kept in one module so a rewrite of the copy touches no component, no stylesheet and no test
 * that asserts layout.
 *
 * Every claim here must be true of what v1 ships and have a substantiation record, in the
 * OpenSpec change that sets the text (REQ-LANDING-010).
 *
 * No product-domain term appears as a label or a named feature (REQ-LANDING-001). Deal,
 * Scenario, Lever, Path and Recommendation belong to a product the visitor cannot use yet, and
 * naming them as features would promise a vocabulary nothing on the page can explain. Plain
 * English is fine: "whether a deal works" names no feature.
 */

export const BRAND = {
  /** Set as typed — the wordmark is uppercase by design, not by text-transform. */
  wordmark: 'TRABEATE',
  name: 'Trabeate',
} as const;

/** Rendered into the prerendered HTML at build time, which is the whole point of prerendering:
 *  a link-preview crawler runs no JavaScript and would otherwise receive an empty shell. */
export const META = {
  title: 'Trabeate — decision confidence for real-estate investors',
  description:
    'Trabeate shows real-estate investors whether a deal’s numbers work against their own ' +
    'criteria, and what would need to change if they don’t. Join the waitlist.',
  url: 'https://trabeate.com',
} as const;

export const HERO = {
  kicker: 'EARLY ACCESS',
  heading: 'Decide with confidence, not a guess.',
  lede:
    'See whether a deal works, why, and what would need to change if it doesn’t, with every number ' +
    'showing where it came from. Join the waitlist to be first when we open access.',
} as const;

export const WAITLIST_COPY = {
  fieldLabel: 'Email address',
  placeholder: 'you@example.com',
  submit: 'Join the waitlist',
  submitting: 'Joining…',
  finePrint: 'Free while in beta. One email when your access is ready.',

  /** Shown for a first signup and a repeat signup alike. The wording deliberately does not
   *  claim the visitor was *added*, because a repeat signup adds nothing — and telling the two
   *  apart is exactly what REQ-WAITLIST-003 forbids. */
  confirmation: 'You’re on the list. We’ll email you when your access is ready.',

  /** Courtesy validation only. The server re-validates and is the only thing that decides
   *  what is stored. */
  invalidEmail: 'Please enter a valid email address.',

  /** The request never left the browser — offline, DNS failure, blocked by an extension.
   *  Nothing here is actionable beyond trying again, so it does not speculate about the cause. */
  networkError: 'Network error — please check your connection and try again.',

  /** Used when the server fails and its response is missing, malformed, or not ours. Never
   *  carries internal detail: a stack trace on a public page is reconnaissance, and a visitor
   *  cannot act on one anyway. */
  genericFailure: 'Something went wrong. Please try again.',
} as const;

export const COLONNADE_COPY = {
  /** Shown only where hovering is possible; hidden entirely on touch and under reduced motion. */
  hint: 'HOVER TO ASSEMBLE',
  /** The structure is decorative. Announcing its parts to a screen reader would be noise, so it
   *  is hidden from the accessibility tree and this label exists only to say why. */
  ariaHiddenReason: 'decorative',
} as const;

/** Rendered as `© <year> Trabeate`. Fixed rather than computed from the clock: a prerendered
 *  page bakes in whatever the build machine's date was, so `new Date()` here would produce a
 *  year that silently drifts from the deploy rather than one anybody chose. */
export const COPYRIGHT_YEAR = 2026;
